/**
 * API Service - Handles all backend communication
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Token management
export const getToken = () => localStorage.getItem('upim_token');
export const setToken = (token) => localStorage.setItem('upim_token', token);
export const removeToken = () => localStorage.removeItem('upim_token');

// Generic fetch wrapper with auth
const fetchAPI = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Error de conexión' }));
    const detail = error.detail;
    const message = typeof detail === 'string' ? detail : Array.isArray(detail)
      ? detail.map(item => `${(item.loc || []).slice(1).join('.')}: ${item.msg}`).join('; ')
      : detail?.motivos?.join('; ') || detail?.detail || `No se pudo completar la solicitud (${response.status}).`;
    const failure = new Error(message);
    failure.status = response.status;
    throw failure;
  }
  
  return response.json();
};

// Auth API
export const authAPI = {
  register: (data) => fetchAPI('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  login: (email, password) => fetchAPI('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  }),
  
  getProfile: () => fetchAPI('/auth/me'),

  // FASE A — seguridad del acceso institucional
  verifyTotpLogin: (pendingToken, code, backupCode) => fetchAPI('/auth/2fa/verify-login', {
    method: 'POST',
    body: JSON.stringify({ pending_token: pendingToken, code: code || '', backup_code: backupCode || null }),
  }),
  changePasswordRequired: (oldPassword, newPassword) => fetchAPI('/auth/change-password-required', {
    method: 'POST',
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  }),
  totpSetup: () => fetchAPI('/auth/2fa/setup', { method: 'POST' }),
  totpEnable: (code) => fetchAPI('/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) }),
  totpDisable: (password, code) => fetchAPI('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ password, code }) }),
  regenerateBackupCodes: (code) => fetchAPI('/auth/2fa/backup-codes', { method: 'POST', body: JSON.stringify({ code }) }),
  createInvitation: (data) => fetchAPI('/auth/invitations', { method: 'POST', body: JSON.stringify(data) }),
  listInvitations: () => fetchAPI('/auth/invitations'),
  
  googleAuthorizeUrl: (redirectUri) =>
    fetchAPI(`/auth/google/authorize-url?redirect_uri=${encodeURIComponent(redirectUri)}`),
  
  updateProfile: (data) => fetchAPI('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Payment API
export const paymentAPI = {
  getPackages: () => fetchAPI('/payments/packages'),
  
  createCheckout: (packageId, originUrl) => fetchAPI('/payments/checkout', {
    method: 'POST',
    body: JSON.stringify({ package_id: packageId, origin_url: originUrl }),
  }),
  
  checkStatus: (sessionId) => fetchAPI(`/payments/status/${sessionId}`),
  
  getHistory: () => fetchAPI('/payments/history'),
};

// Public API (no auth required)
export const publicAPI = {
  getCarousels: (type) => fetchAPI(`/public/carousels${type ? `?carousel_type=${type}` : ''}`),
  
  getEvents: (upcomingOnly = true) => fetchAPI(`/public/events?upcoming_only=${upcomingOnly}`),
  
  getProviders: (category) => fetchAPI(`/public/providers${category ? `?category=${category}` : ''}`),
  
  getProviderCategories: () => fetchAPI('/public/providers/categories'),
};

// Admin API
export const adminAPI = {
  getStats: () => fetchAPI('/admin/stats'),
  
  getAffiliates: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.offset) searchParams.append('offset', params.offset);
    return fetchAPI(`/admin/affiliates?${searchParams.toString()}`);
  },
  
  getAffiliateDetail: (userId) => fetchAPI(`/admin/affiliates/${userId}`),
  
  getCarousels: (type) => fetchAPI(`/admin/carousels${type ? `?carousel_type=${type}` : ''}`),
  
  createCarouselLogo: (data) => fetchAPI('/admin/carousels', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  updateCarouselLogo: (logoId, data) => fetchAPI(`/admin/carousels/${logoId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  
  deleteCarouselLogo: (logoId) => fetchAPI(`/admin/carousels/${logoId}`, {
    method: 'DELETE',
  }),
  
  getBroadcasts: () => fetchAPI('/admin/broadcasts'),
  
  sendBroadcast: (data) => fetchAPI('/admin/broadcasts', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  
  getProviders: (verifiedOnly = false) => fetchAPI(`/admin/providers?verified_only=${verifiedOnly}`),
};

// Afiliación web (FASE 2)
export const affiliationAPI = {
  publicSections: () => fetchAPI('/affiliation/sections'),
  requirements: (type) => fetchAPI(`/affiliation/requirements?applicant_type=${type}`),
  createRequest: (data) => fetchAPI('/affiliation/requests', { method: 'POST', body: JSON.stringify(data) }),
  resumeGet: (token) => fetchAPI(`/affiliation/requests/resume?token=${encodeURIComponent(token)}`),
  resumePut: (token, data) => fetchAPI(`/affiliation/requests/resume?token=${encodeURIComponent(token)}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminList: (params = '') => fetchAPI(`/affiliation/admin/requests${params}`),
  adminDashboard: (sectionId) => fetchAPI(`/affiliation/admin/dashboard${sectionId ? `?section_id=${sectionId}` : ''}`),
  qroDashboard: (sectionId) => fetchAPI(`/affiliation/admin/qro-dashboard${sectionId ? `?section_id=${sectionId}` : ''}`),
  adminRequests: (params = '') => fetchAPI(`/affiliation/admin/requests${params}`),
  adminBucket: (bucket, sectionId) => {
    const p = new URLSearchParams();
    if (bucket) p.append('bucket', bucket);
    if (sectionId) p.append('section_id', sectionId);
    return fetchAPI(`/affiliation/admin/requests?${p.toString()}`);
  },
  duplicates: (sectionId) => fetchAPI(`/affiliation/admin/duplicates${sectionId ? `?section_id=${sectionId}` : ''}`),
  duplicateCotejo: (id) => fetchAPI(`/affiliation/admin/requests/${id}/duplicate-cotejo`),
  resolveDuplicate: (id, reason) => fetchAPI(`/affiliation/admin/requests/${id}/resolve-duplicate`, { method: 'POST', body: JSON.stringify({ status: 'resolved', reason }) }),
  adminDetail: (id) => fetchAPI(`/affiliation/admin/requests/${id}`),
  setStatus: (id, data) => fetchAPI(`/affiliation/admin/requests/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  resumeRenew: (token) => fetchAPI(`/affiliation/requests/resume/renew?token=${encodeURIComponent(token)}`, { method: 'POST' }),
  resumeRevoke: (token) => fetchAPI(`/affiliation/requests/resume/revoke?token=${encodeURIComponent(token)}`, { method: 'POST' }),
  resumeDocs: (token) => fetchAPI(`/affiliation/requests/resume/documents?token=${encodeURIComponent(token)}`),

  // FASE A — núcleo QRO
  activationCheck: (id) => fetchAPI(`/affiliation/admin/requests/${id}/activation-check`),
  activate: (id, exceptionReason) => fetchAPI(`/affiliation/admin/requests/${id}/activate`, {
    method: 'POST', body: JSON.stringify({ exception_reason: exceptionReason || null }),
  }),
  directory: (params = '') => fetchAPI(`/affiliation/admin/directory${params}`),
  pagos: (sectionId) => fetchAPI(`/affiliation/admin/pagos${sectionId ? `?section_id=${sectionId}` : ''}`),

  // BLOQUE C/D — renovaciones y conciliación territorial
  renovaciones: (sectionId) => fetchAPI(`/affiliation/admin/renovaciones${sectionId ? `?section_id=${sectionId}` : ''}`),
  avisosRenovacion: (dryRun = true, sectionId) => fetchAPI('/affiliation/admin/renovaciones/avisos', {
    method: 'POST', body: JSON.stringify({ dry_run: dryRun, section_id: sectionId || null }),
  }),
  iniciarRenovacion: (id, motivo) => fetchAPI(`/affiliation/admin/requests/${id}/renovacion`, {
    method: 'POST', body: JSON.stringify({ motivo: motivo || null }),
  }),
  // Descarga autenticada del CSV (el token viaja en la cabecera, nunca en la URL)
  descargarPagosCsv: async (sectionId) => {
    const url = `${API_URL}/api/affiliation/admin/pagos/export.csv${sectionId ? `?section_id=${sectionId}` : ''}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error(res.status === 403 ? 'Sin permiso sobre esa sección' : 'No se pudo exportar');
    return { blob: await res.blob(), registros: res.headers.get('X-Total-Registros') };
  },
  docs: (id) => fetchAPI(`/affiliation/admin/requests/${id}/documents`),
  crearCheckout: (payload) => fetchAPI('/affiliation/pago/checkout', {
    method: 'POST', body: JSON.stringify(payload),
  }),
  estadoPago: (sessionId) => fetchAPI(`/affiliation/pago/status/${sessionId}`),

  // PORTAL DEL AFILIADO — alcance estrictamente propio (sin identificadores en la URL)
  miAfiliacion: () => fetchAPI('/affiliation/mi-afiliacion'),
  miRenovacionCheckout: (payload) => fetchAPI('/affiliation/mi-afiliacion/renovacion/checkout', {
    method: 'POST', body: JSON.stringify(payload),
  }),
};

// P3A — CRM territorial (vistas centrales por sección; alcance validado en backend)
export const crmTerritorialAPI = {
  asesores: (s) => fetchAPI(`/crm/territorial/asesores${s ? `?section_id=${s}` : ''}`),
  prospectos: (s) => fetchAPI(`/crm/territorial/prospectos${s ? `?section_id=${s}` : ''}`),
  clientes: (s) => fetchAPI(`/crm/territorial/clientes${s ? `?section_id=${s}` : ''}`),
  propiedades: (s) => fetchAPI(`/crm/territorial/propiedades${s ? `?section_id=${s}` : ''}`),
  actividades: (s) => fetchAPI(`/crm/territorial/actividades${s ? `?section_id=${s}` : ''}`),
  resumen: (s) => fetchAPI(`/crm/territorial/resumen${s ? `?section_id=${s}` : ''}`),
  registrarNota: (data) => fetchAPI('/crm/territorial/notas', {
    method: 'POST', body: JSON.stringify(data),
  }),
  // P3B — reasignación auditada y expediente del prospecto
  reasignarProspecto: (id, data) => fetchAPI(`/crm/territorial/prospectos/${id}/reasignar`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  historialProspecto: (id) => fetchAPI(`/crm/territorial/prospectos/${id}/historial`),
  // P3C — perfil de necesidad y emparejamiento determinista
  actualizarPerfilCliente: (id, data) => fetchAPI(`/crm/territorial/clientes/${id}/perfil`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  coincidenciasCliente: (id) => fetchAPI(`/crm/territorial/clientes/${id}/coincidencias`),
  crearOportunidad: (id, data) => fetchAPI(`/crm/territorial/clientes/${id}/oportunidad`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  oportunidades: (s, dias) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    if (dias) q.set('inactividad_dias', dias);
    return fetchAPI(`/crm/territorial/oportunidades${q.toString() ? `?${q}` : ''}`);
  },
  // P3F — avance/retroceso de etapa auditado
  cambiarEtapaOportunidad: (id, data) => fetchAPI(
    `/crm/territorial/oportunidades/${id}/etapa`, { method: 'POST', body: JSON.stringify(data) }),
  historialEtapas: (id) => fetchAPI(`/crm/territorial/oportunidades/${id}/historial-etapas`),
  // P3G — reporte territorial de desempeño por asesor
  desempeno: (s, dias) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    if (dias) q.set('inactividad_dias', dias);
    return fetchAPI(`/crm/territorial/desempeno${q.toString() ? `?${q}` : ''}`);
  },
  descargarDesempenoCsv: async (s, dias) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    if (dias) q.set('inactividad_dias', dias);
    const res = await fetch(
      `${API_URL}/api/crm/territorial/desempeno/export.csv${q.toString() ? `?${q}` : ''}`,
      { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('No autorizado para exportar');
    const url = window.URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'desempeno_territorial.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  // P3H — metas territoriales y semáforo de cumplimiento
  metas: (s, incluirInactivas) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    if (incluirInactivas) q.set('incluir_inactivas', 'true');
    return fetchAPI(`/crm/territorial/metas${q.toString() ? `?${q}` : ''}`);
  },
  crearMeta: (data) => fetchAPI('/crm/territorial/metas',
    { method: 'POST', body: JSON.stringify(data) }),
  actualizarMeta: (id, data) => fetchAPI(`/crm/territorial/metas/${id}`,
    { method: 'PUT', body: JSON.stringify(data) }),
  desactivarMeta: (id) => fetchAPI(`/crm/territorial/metas/${id}`, { method: 'DELETE' }),
  // P3J — captura manual de prospecto territorial
  catalogosProspecto: () => fetchAPI('/crm/territorial/prospectos/catalogos'),
  capturarProspecto: (data) => fetchAPI('/crm/territorial/prospectos',
    { method: 'POST', body: JSON.stringify(data) }),
  // P3K — reasignación masiva auditada de cartera (lote atómico con previsualización)
  reasignarMasivo: (data) => fetchAPI('/crm/territorial/prospectos/reasignar-masivo',
    { method: 'POST', body: JSON.stringify(data) }),
  // P3L — captura y edición territorial de propiedad (sólo datos estructurados)
  catalogosPropiedad: () => fetchAPI('/crm/territorial/propiedades/catalogos'),
  propiedadDetalle: (id) => fetchAPI(`/crm/territorial/propiedades/${id}`),
  crearPropiedad: (data) => fetchAPI('/crm/territorial/propiedades',
    { method: 'POST', body: JSON.stringify(data) }),
  actualizarPropiedad: (id, data) => fetchAPI(`/crm/territorial/propiedades/${id}`,
    { method: 'PUT', body: JSON.stringify(data) }),
  // P3I — visor territorial de bitácora auditada (sin PII)
  bitacora: (s, f = {}) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
    return fetchAPI(`/crm/territorial/bitacora${q.toString() ? `?${q}` : ''}`);
  },
  descargarBitacoraCsv: async (s, f = {}) => {
    const q = new URLSearchParams();
    if (s) q.set('section_id', s);
    Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
    const res = await fetch(
      `${API_URL}/api/crm/territorial/bitacora/export.csv${q.toString() ? `?${q}` : ''}`,
      { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('No autorizado para exportar');
    const url = window.URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bitacora_territorial.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  documentosProspecto: (id) => fetchAPI(`/crm/territorial/prospectos/${id}/documentos`),
  subirDocumentoProspecto: async (id, file, tipo) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', tipo || 'otro');
    const res = await fetch(`${API_URL}/api/crm/territorial/prospectos/${id}/documentos`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Error al subir');
    return res.json();
  },
  descargarDocumento: async (docId) => {    const res = await fetch(`${API_URL}/api/crm/territorial/documentos/${docId}/descargar`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error('No autorizado para descargar');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },
};

// P13B — Agente 1 (gestor de inventario): guion determinista, sin IA y sin canales externos
export const agenteInventarioAPI = {
  guion: () => fetchAPI('/crm/agentes/inventario/guion'),
  sesiones: (estado) => fetchAPI(
    `/crm/agentes/inventario/sesiones${estado ? `?estado=${estado}` : ''}`),
  crearSesion: (data) => fetchAPI('/crm/agentes/inventario/sesiones',
    { method: 'POST', body: JSON.stringify(data) }),
  sesion: (id) => fetchAPI(`/crm/agentes/inventario/sesiones/${id}`),
  responder: (id, data) => fetchAPI(`/crm/agentes/inventario/sesiones/${id}/responder`,
    { method: 'POST', body: JSON.stringify(data) }),
  confirmar: (id, data = {}) => fetchAPI(`/crm/agentes/inventario/sesiones/${id}/confirmar`,
    { method: 'POST', body: JSON.stringify(data) }),
  handoff: (id, data) => fetchAPI(`/crm/agentes/inventario/sesiones/${id}/handoff`,
    { method: 'POST', body: JSON.stringify(data) }),
  cancelar: (id, data) => fetchAPI(`/crm/agentes/inventario/sesiones/${id}/cancelar`,
    { method: 'POST', body: JSON.stringify(data) }),
};

// P13C — Agente 2 (atención a clientes): perfil determinista + coincidencias P3C + interés
export const agenteAtencionAPI = {
  guion: () => fetchAPI('/crm/agentes/atencion/guion'),
  crearSesion: (data) => fetchAPI('/crm/agentes/atencion/sesiones',
    { method: 'POST', body: JSON.stringify(data) }),
  responder: (id, data) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/responder`,
    { method: 'POST', body: JSON.stringify(data) }),
  confirmar: (id, data = {}) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/confirmar`,
    { method: 'POST', body: JSON.stringify(data) }),
  coincidencias: (id) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/coincidencias`),
  interes: (id, data) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/interes`,
    { method: 'POST', body: JSON.stringify(data) }),
  handoff: (id, data) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/handoff`,
    { method: 'POST', body: JSON.stringify(data) }),
  cancelar: (id, data) => fetchAPI(`/crm/agentes/atencion/sesiones/${id}/cancelar`,
    { method: 'POST', body: JSON.stringify(data) }),
};

// P13D — Agente 3 (prospectos de afiliación): informar + alta P3J + dedup
export const agenteAfiliacionAPI = {
  info: () => fetchAPI('/crm/agentes/afiliacion/info'),
  guion: () => fetchAPI('/crm/agentes/afiliacion/guion'),
  crearSesion: (data) => fetchAPI('/crm/agentes/afiliacion/sesiones',
    { method: 'POST', body: JSON.stringify(data) }),
  responder: (id, data) => fetchAPI(`/crm/agentes/afiliacion/sesiones/${id}/responder`,
    { method: 'POST', body: JSON.stringify(data) }),
  confirmar: (id, data = {}) => fetchAPI(`/crm/agentes/afiliacion/sesiones/${id}/confirmar`,
    { method: 'POST', body: JSON.stringify(data) }),
  handoff: (id, data) => fetchAPI(`/crm/agentes/afiliacion/sesiones/${id}/handoff`,
    { method: 'POST', body: JSON.stringify(data) }),
  cancelar: (id, data) => fetchAPI(`/crm/agentes/afiliacion/sesiones/${id}/cancelar`,
    { method: 'POST', body: JSON.stringify(data) }),
};

// P13E — Asistencia LLM opcional (desactivada por defecto; sólo estado/interpretación)
export const agenteAsistenciaAPI = {
  estado: () => fetchAPI('/crm/agentes/asistencia/estado'),
  interpretar: (data) => fetchAPI('/crm/agentes/asistencia/interpretar',
    { method: 'POST', body: JSON.stringify(data) }),
  config: () => fetchAPI('/crm/agentes/asistencia/config'),
};

// P13G — Panel de supervisión de sesiones de los agentes (sólo lectura, determinista)
export const agenteSupervisionAPI = {
  resumen: (filtros = {}) => fetchAPI(`/crm/agentes/supervision/resumen?${new URLSearchParams(
    Object.entries(filtros).filter(([, v]) => v)).toString()}`),
  sesiones: (filtros = {}) => fetchAPI(`/crm/agentes/supervision/sesiones?${new URLSearchParams(
    Object.entries(filtros).filter(([, v]) => v)).toString()}`),
  detalle: (id) => fetchAPI(`/crm/agentes/supervision/sesiones/${id}`),
  revision: (id, data) => fetchAPI(`/crm/agentes/supervision/sesiones/${id}/revision`,
    { method: 'POST', body: JSON.stringify(data) }),
  resolverError: (errorId, data) => fetchAPI(`/crm/agentes/supervision/errores/${errorId}/resolucion`,
    { method: 'POST', body: JSON.stringify(data) }),
  tendencias: (filtros = {}) => fetchAPI(`/crm/agentes/supervision/tendencias?${
    new URLSearchParams(Object.entries(filtros).filter(([, v]) => v)).toString()}`),
  exportarCsv: async (filtros = {}) => {
    const qs = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v)).toString();
    const resp = await fetch(`${API_URL}/api/crm/agentes/supervision/exportar.csv?${qs}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!resp.ok) throw new Error('No se pudo exportar el CSV');
    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supervision_agentes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  exportarTendenciaCsv: async (filtros = {}) => {
    const qs = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v)).toString();
    const resp = await fetch(`${API_URL}/api/crm/agentes/supervision/tendencias.csv?${qs}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!resp.ok) throw new Error('No se pudo exportar la tendencia');
    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tendencia_agentes.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// Organización Nacional (FASE 1)
export const orgAPI = {
  myContext: () => fetchAPI('/org/me/context'),
  getSections: () => fetchAPI('/org/sections'),
  createSection: (data) => fetchAPI('/org/sections', { method: 'POST', body: JSON.stringify(data) }),
  getSectionDashboard: (id) => fetchAPI(`/org/sections/${id}/dashboard`),
  getRoles: () => fetchAPI('/org/roles'),
  getPermissions: () => fetchAPI('/org/permissions'),
  getAssignments: () => fetchAPI('/org/assignments'),
  createAssignment: (data) => fetchAPI('/org/assignments', { method: 'POST', body: JSON.stringify(data) }),
  revokeAssignment: (id) => fetchAPI(`/org/assignments/${id}/revoke`, { method: 'POST' }),
  getAppointments: () => fetchAPI('/org/appointments'),
  getAudit: (sectionId) => fetchAPI(`/org/audit${sectionId ? `?section_id=${sectionId}` : ''}`),
};

// Axios-like API wrapper for compatibility
const api = {
  get: async (endpoint) => {
    const data = await fetchAPI(endpoint);
    return { data };
  },
  post: async (endpoint, body) => {
    const data = await fetchAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { data };
  },
  put: async (endpoint, body) => {
    const data = await fetchAPI(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return { data };
  },
  delete: async (endpoint) => {
    const data = await fetchAPI(endpoint, {
      method: 'DELETE',
    });
    return { data };
  },
  auth: authAPI,
  payment: paymentAPI,
  public: publicAPI,
  admin: adminAPI,
};

export default api;

// F14D — Audiencias y segmentación territorial determinista de marketing (sin envíos externos)
export const marketingAudienciasAPI = {
  catalogos: () => fetchAPI('/marketing/audiencias/catalogos'),
  listar: (filtros = {}) => fetchAPI(`/marketing/audiencias?${new URLSearchParams(
    Object.entries(filtros).filter(([, v]) => v)).toString()}`),
  detalle: (id) => fetchAPI(`/marketing/audiencias/${id}`),
  crear: (data) => fetchAPI('/marketing/audiencias',
    { method: 'POST', body: JSON.stringify(data) }),
  editar: (id, data) => fetchAPI(`/marketing/audiencias/${id}`,
    { method: 'PATCH', body: JSON.stringify(data) }),
  reemplazarReglas: (id, data) => fetchAPI(`/marketing/audiencias/${id}/reglas`,
    { method: 'PUT', body: JSON.stringify(data) }),
  historial: (id) => fetchAPI(`/marketing/audiencias/${id}/historial`),
  estimar: (id) => fetchAPI(`/marketing/audiencias/${id}/estimacion`, { method: 'POST' }),
  vistaPrevia: (id, limite = 10) => fetchAPI(
    `/marketing/audiencias/${id}/vista-previa?limite=${limite}`),
  archivar: (id) => fetchAPI(`/marketing/audiencias/${id}/archivado`, { method: 'POST' }),
  supresiones: (seccion) => fetchAPI(`/marketing/audiencias/supresiones/lista${
    seccion ? `?seccion_id=${seccion}` : ''}`),
  altaSupresion: (data) => fetchAPI('/marketing/audiencias/supresiones/alta',
    { method: 'POST', body: JSON.stringify(data) }),
};

// FISCAL-1A — perfil fiscal propio, solicitud de factura y descargas autenticadas.
// Los archivos fiscales NUNCA se abren por URL simple: exigen el token en la cabecera.
const descargaPrivada = async (ruta, nombre) => {
  const res = await fetch(`${API_URL}/api${ruta}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || 'No autorizado para descargar este archivo');
  }
  const url = window.URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const fiscalAPI = {
  catalogos: () => fetchAPI('/fiscal/catalogos'),
  perfil: () => fetchAPI('/fiscal/perfil'),
  guardarPerfil: (data) => fetchAPI('/fiscal/perfil',
    { method: 'PUT', body: JSON.stringify(data) }),
  misPagos: () => fetchAPI('/fiscal/mis-pagos'),
  misSolicitudes: () => fetchAPI('/fiscal/mis-solicitudes'),
  requerirFactura: (folio) => fetchAPI(
    `/fiscal/pagos/${encodeURIComponent(folio)}/requiero-factura`, { method: 'POST' }),
  subirConstancia: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('confirmo_datos', 'true');
    const res = await fetch(`${API_URL}/api/fiscal/constancia`, {
      method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((d?.detail?.errores || [d?.detail])[0] || 'No se pudo cargar');
    return d;
  },
  descargarConstancia: (docId, nombre) => descargaPrivada(
    `/fiscal/constancia/${docId}`, nombre || 'constancia.pdf'),
  descargarCfdi: (solicitudId, archivoId, nombre) => descargaPrivada(
    `/fiscal/solicitudes/${solicitudId}/archivo/${archivoId}`, nombre || 'cfdi'),
};
